import type { GridColDef } from '@mui/x-data-grid';
import type { Partnership } from '../types';
import { CellContainer, CellTypography } from '../components/AccountsGrid.styles';
import { useMemo } from 'react';
import { IconButton } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';

type PartnershipsGridField =
  | 'tag'
  | 'facebook_page_name'
  | 'instagram_page_name'
  | 'app_page_name'
  | 'status'
  | 'actions';

interface PartnershipsGridComponent extends Omit<GridColDef<Partnership>, 'field'> {
  field: PartnershipsGridField;
}

const columnsAdditionalOptions = {
  disableColumnFilter: true,
  disableColumnMenu: true,
};

interface UsePartnershipsColumnsProps {
  canEditAdTag: boolean;
  onEditAdTag: (partnership: Partnership) => void;
}

export const usePartnershipsColumns = ({
  canEditAdTag,
  onEditAdTag,
}: UsePartnershipsColumnsProps): PartnershipsGridComponent[] => {

  return useMemo<PartnershipsGridComponent[]>(
    () => [
      {
        field: 'facebook_page_name',
        headerName: 'Partner Facebook page',
        flex: 1,
        minWidth: 220,
        sortable: false,
        ...columnsAdditionalOptions,
        renderCell: ({ row }) => (
          <CellContainer>
            <CellTypography variant={'body2'}>
              {row.facebook_page_name ?? row.facebook_page_id ?? '-'}
            </CellTypography>
          </CellContainer>
        ),
      },
      {
        field: 'instagram_page_name',
        headerName: 'Partner Instagram page',
        flex: 1,
        minWidth: 220,
        sortable: false,
        ...columnsAdditionalOptions,
        renderCell: ({ row }) => (
          <CellContainer>
            <CellTypography variant={'body2'}>
              {row.instagram_page_name ?? row.instagram_page_id ?? '-'}
            </CellTypography>
          </CellContainer>
        ),
      },
      {
        field: 'app_page_name',
        headerName: 'Welltech app page',
        flex: 1,
        minWidth: 220,
        sortable: false,
        ...columnsAdditionalOptions,
        renderCell: ({ row }) => (
          <CellContainer>
            <CellTypography variant={'body2'}>
              {row.app_page_name ?? '-'}
            </CellTypography>
          </CellContainer>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.8,
        minWidth: 140,
        sortable: false,
        ...columnsAdditionalOptions,
        renderCell: ({ row }) => (
          <CellContainer>
            <CellTypography variant={'body2'}>{row.status}</CellTypography>
          </CellContainer>
        ),
      },
      {
        field: 'actions',
        headerName: 'Partnership ad tag',
        flex: 1,
        minWidth: 220,
        sortable: false,
        ...columnsAdditionalOptions,
        renderCell: ({ row }) => (
          <CellContainer>
            <CellTypography variant={'body2'}>{row.tag ?? '-'}</CellTypography>
            {canEditAdTag && (
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onEditAdTag(row);
                }}
              >
                <EditOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </CellContainer>
        ),
      },
    ],
    [canEditAdTag, onEditAdTag],
  );
};
